import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Drawer, List, ListItemButton, ListItemText, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getChatExample, listChatExamples } from './api';
import ChatExampleView from './ChatExampleView';
import type { ChatExample, ExampleSummary } from './types';
export type ChatExamplesBrowserProps = {
  canUseChatbot?: boolean;
  showTrigger?: boolean;
  open?: boolean;
  accessScope?: string;
  allowedWorkflowTypes?: string[] | null;
  onOpenWorkflow?: (flowId: string, title: string, workflowType: string) => void;
  onClose?: () => void;
  onNewChat?: () => void;
  onOpenChats?: () => void;
};
type Selection = {
  id: string | null;
  revision?: string;
};
// Retire former example deep links without changing legitimate app parameters,
// their encoding, the fragment, or the existing history entry's state.
function removeLegacyExampleParameters() {
  const { pathname, search, hash } = window.location;
  const parts = search.slice(1).split('&');
  const kept = parts.filter(part => {
    try {
      const key = decodeURIComponent(part.split('=')[0].replace(/\+/g, ' '));
      return key !== 'chatExample' && key !== 'chatExampleRevision';
    } catch {
      return true;
    }
  });
  if (kept.length !== parts.length) {
    const query = kept.join('&');
    window.history.replaceState(window.history.state, '', `${pathname}${query ? `?${query}` : ''}${hash}`);
  }
}
export default function ChatExamplesBrowser({
  canUseChatbot = false,
  showTrigger = true,
  open,
  accessScope,
  allowedWorkflowTypes,
  onOpenWorkflow,
  onClose,
  onNewChat,
  onOpenChats
}: ChatExamplesBrowserProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localOpen, setLocalOpen] = useState(false);
  const active = open ?? localOpen;
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<Selection>({ id: null });
  const [catalog, setCatalog] = useState<ExampleSummary[]>([]);
  const [detail, setDetail] = useState<ChatExample | null>(null);
  const [catalogError, setCatalogError] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [catalogRetry, setCatalogRetry] = useState(0);
  const [detailRetry, setDetailRetry] = useState(0);
  const scope = JSON.stringify([accessScope, canUseChatbot, allowedWorkflowTypes]);
  const [catalogScope, setCatalogScope] = useState<string | null>(null);
  const [detailScope, setDetailScope] = useState<string | null>(null);
  const permitted = useCallback((item: ExampleSummary) => item.kind === 'qa' ? canUseChatbot : !allowedWorkflowTypes || allowedWorkflowTypes.includes(item.workflow_type || ''), [canUseChatbot, allowedWorkflowTypes]);
  const available = catalogScope === scope ? catalog.filter(permitted) : [];
  useEffect(removeLegacyExampleParameters, []);
  useEffect(() => {
    if (!active) setSelection({ id: null });
    setMobileListOpen(active && isMobile);
  }, [active, isMobile]);
  useEffect(() => {
    let current = true;
    setCatalog([]);
    setCatalogScope(null);
    setCatalogError(false);
    setCatalogLoading(false);
    if (!active || !canUseChatbot) return;
    setCatalogLoading(true);
    listChatExamples().then(value => {
      if (!current) return;
      setCatalog(value.filter(permitted));
      setCatalogScope(scope);
    }).catch(() => {
      if (current) setCatalogError(true);
    }).finally(() => {
      if (current) setCatalogLoading(false);
    });
    return () => { current = false; };
  }, [active, scope, catalogRetry, permitted, canUseChatbot]);
  useEffect(() => {
    let current = true;
    setDetail(null);
    setDetailScope(null);
    setDetailError(false);
    setDetailLoading(false);
    if (!active || !canUseChatbot || !selection.id || catalogScope !== scope) return;
    // The current authorized list must still contain this exact pinned sample.
    if (!catalog.some(item => item.example_id === selection.id && item.revision === selection.revision && permitted(item))) {
      setDetailError(true);
      return;
    }
    setDetailLoading(true);
    getChatExample(selection.id, selection.revision).then(value => {
      if (!current) return;
      if (permitted(value)) { setDetail(value); setDetailScope(scope); }
      else setDetailError(true);
    }).catch(() => {
      if (current) setDetailError(true);
    }).finally(() => {
      if (current) setDetailLoading(false);
    });
    return () => { current = false; };
  }, [active, selection.id, selection.revision, scope, catalogScope, catalog, detailRetry, permitted, canUseChatbot]);
  useEffect(() => { conversationRef.current?.scrollTo?.({ top: 0 }); }, [selection.id]);
  const leave = () => {
    setLocalOpen(false);
    setSelection({ id: null });
    onClose?.();
  };
  const choose = (item: ExampleSummary) => {
    setSelection({ id: item.example_id, revision: item.revision });
    setMobileListOpen(false);
  };
  const renderRail = () => <Paper role="navigation" aria-label="Example sessions"
    variant="outlined" sx={{ width: isMobile ? '100%' : 280, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography component="h2" variant="subtitle1" fontWeight={600}>Chat examples</Typography>
      <Typography variant="caption" color="text.secondary">Read-only example conversations</Typography>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {catalogLoading && <Typography role="status" sx={{ p: 2 }}>Loading chat examples…</Typography>}
      {catalogError && <Alert severity="info">Examples are unavailable. <Button onClick={() => setCatalogRetry(value => value + 1)}>Retry</Button></Alert>}
      {!catalogLoading && !catalogError && catalogScope === scope && available.length === 0 && <Typography sx={{ p: 2 }} color="text.secondary">No chat examples are available for your access.</Typography>}
      <List dense disablePadding>{available.map(item => <ListItemButton key={item.example_id}
        selected={selection.id === item.example_id} aria-current={selection.id === item.example_id ? 'true' : undefined}
        aria-label={`Open ${item.title}`} onClick={() => choose(item)} sx={{ minHeight: 48 }}>
        <ListItemText primary={item.title} secondary="Example" primaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }} />
      </ListItemButton>)}</List>
    </Box>
  </Paper>;
  if (!canUseChatbot) return null;
  if (!active) return showTrigger ? <Button onClick={() => setLocalOpen(true)}>Chat examples</Button> : null;
  return <Paper variant="outlined" aria-label="Chat examples" sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, minWidth: 0,
    height: 'min(800px, 75dvh)', minHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <Box component="nav" aria-label="Example navigation" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, mb: 1.5 }}>
      {onOpenChats && <Button startIcon={<ArrowBackIcon />} onClick={() => { leave(); onOpenChats(); }}>Previous chats</Button>}
      {onNewChat && <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => { leave(); onNewChat(); }} sx={{ ml: 'auto' }}>New request</Button>}
    </Box>
    {isMobile && <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
      <Button size="small" variant="outlined" onClick={() => setMobileListOpen(true)}>Examples</Button>
    </Box>}
    <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, minWidth: 0 }}>
      {!isMobile && renderRail()}
      <Box sx={{ flex: 1, minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box ref={conversationRef} sx={{ p: { xs: 1.5, sm: 2 }, minHeight: 0, flex: 1, overflowY: 'auto', overflowWrap: 'anywhere' }}>
          {!selection.id && <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 1 }}>
            <Typography variant="h6">Choose an example conversation</Typography>
            <Typography color="text.secondary">{isMobile ? 'Open Examples to choose a saved conversation.' : 'Select a name from the left to explore its read-only forms and answers.'}</Typography>
          </Box>}
          {selection.id && (catalogLoading || detailLoading) && <Typography role="status">Loading chat example…</Typography>}
          {selection.id && detailError && !catalogLoading && <Alert severity="info">This chat example is unavailable. <Button onClick={() => setDetailRetry(value => value + 1)}>Retry</Button></Alert>}
          {detail && detail.example_id === selection.id && detail.revision === selection.revision && detailScope === scope && catalogScope === scope && !detailLoading && !detailError && permitted(detail) && <>
            <ChatExampleView key={`${detail.example_id}:${detail.revision}`} example={detail} />
            {detail.workflow && onOpenWorkflow && <Button sx={{ mt: 2 }} onClick={() => { leave(); onOpenWorkflow(detail.workflow!.flow_id, detail.title, detail.workflow!.workflow_type); }}>View workflow example</Button>}
          </>}
        </Box>
      </Box>
    </Box>
    {isMobile && <Drawer anchor="left" open={mobileListOpen} onClose={() => setMobileListOpen(false)}
      sx={{ zIndex: theme => Math.max(theme.zIndex.modal, 2200) + 50 }}
      PaperProps={{ role: 'dialog', 'aria-label': 'Example sessions', sx: { width: 'min(88vw, 320px)', p: 1.5 } }}>
      {renderRail()}
    </Drawer>}
  </Paper>;
}
